import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Stack,
    Text,
    Card,
    Checkbox,
    TabsProvider,
    Tabs,
    Tab,
    TabPanels,
    TabPanel,
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    Drawer,
    Columns,
    Column,
    Divider,
    Badge,
    Inline,
} from 'braid-design-system';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../hooks/useToast';
import {
    CustomerGuarantorItem,
    CustomerLoanItem,
    CustomerLoanSummary,
    getCustomerLoanSummary,
} from '../../../services/loanService';
import { PlTransactionResponse, getLastTransactionsListOfAccount } from '../../../services/transactionService';
import Loading from '../../Loading';
import { formatCurrency } from '../../../utils/currencyUtils';

interface GuaranteesTabProps {
    customerNumber: string;
}

const GuaranteesTab: React.FC<GuaranteesTabProps> = ({ customerNumber }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const [loanData, setLoanData] = useState<CustomerLoanSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showClosedLoans, setShowClosedLoans] = useState(false);
    const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
    const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(null);
    const [transactionsOpen, setTransactionsOpen] = useState(false);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactions, setTransactions] = useState<PlTransactionResponse[]>([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await getCustomerLoanSummary(customerNumber);
                setLoanData(response);
            } catch (err: any) {
                setError(
                    err?.response?.data?.detail ||
                    err?.message ||
                    t('page.approve_mobile_app_customer.customer_details.guarantees.error.load_failed', {
                        defaultValue: 'Failed to load guarantees data.',
                    })
                );
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [customerNumber]);

    useEffect(() => {
        if (!transactionsOpen || !selectedLoanId) return;
        let isMounted = true;
        setTransactionsLoading(true);
        getLastTransactionsListOfAccount(selectedLoanId, 10)
            .then((data) => {
                if (!isMounted) return;
                const sorted = [...(data || [])].sort((a, b) => b.id - a.id);
                setTransactions(sorted);
            })
            .catch((error: any) => {
                if (!isMounted) return;
                toast.showError(error?.message ?? 'Failed to load account transactions');
                setTransactions([]);
            })
            .finally(() => {
                if (isMounted) {
                    setTransactionsLoading(false);
                }
            });
        return () => {
            isMounted = false;
        };
    }, [selectedLoanId, toast, transactionsOpen]);

    const handleAccountClick = useCallback((loanId: number, accountNumber: string | null) => {
        setSelectedLoanId(loanId);
        setSelectedAccountNumber(accountNumber);
        setTransactionsOpen(true);
    }, []);

    const ordinalLabel = (index: number): string => {
        const labels = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];
        if (index < labels.length) {
            return labels[index];
        }
        return `${index + 1}th`;
    };

    const formatDate = (value: string | null | undefined): string => {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleDateString();
    };

    const renderMyLoanCards = (items: CustomerLoanItem[], emptyMessage: string) => {
        if (!items.length) {
            return (
                <Card>
                    <Box padding="large" display="flex" justifyContent="center" alignItems="center">
                        <Text tone="secondary">{emptyMessage}</Text>
                    </Box>
                </Card>
            );
        }

        return (
            <Stack space="medium">
                {items.map((loan, loanIndex) => {
                    const safeGuarantors = Array.isArray(loan.guarantors) ? loan.guarantors : [];
                    const pastDueVal = loan.past_due_amount ?? loan.past_due;
                    const txDateVal = loan.last_transaction_date ?? loan.transaction_date;

                    return (
                        <Card key={`${loan.loan_id}-${loanIndex}`}>
                            <Stack space="medium">
                                <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                                    <Stack space="xsmall">
                                        <Box
                                            cursor="pointer"
                                            onClick={() => handleAccountClick(loan.loan_id, loan.loan_account_number)}
                                        >
                                            <Text tone="info" weight="strong" size="large">
                                                {loan.loan_account_number || '—'}
                                            </Text>
                                        </Box>
                                        <Text tone="secondary" size="small">
                                            {loan.product_name || '—'}
                                        </Text>
                                    </Stack>
                                    {pastDueVal != null && pastDueVal > 0 ? (
                                        <Badge tone="critical">Past Due</Badge>
                                    ) : (
                                        <Badge tone="positive">Active</Badge>
                                    )}
                                </Box>

                                <Divider />

                                <Columns space="medium" collapseBelow="tablet">
                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Balance</Text>
                                                <Text weight="strong" size="standard">
                                                    {loan.balance == null ? '—' : formatCurrency(loan.balance)}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Issued Amount</Text>
                                                <Text size="standard">
                                                    {loan.issued_amount == null ? '—' : formatCurrency(loan.issued_amount)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>

                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Past Due</Text>
                                                <Text
                                                    weight="strong"
                                                    size="standard"
                                                    tone={pastDueVal != null && pastDueVal > 0 ? 'critical' : 'neutral'}
                                                >
                                                    {pastDueVal == null ? '—' : formatCurrency(pastDueVal)}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Installment</Text>
                                                <Text size="standard">
                                                    {loan.installment == null ? '—' : formatCurrency(loan.installment)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>

                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Interest Rate / Period</Text>
                                                <Text size="standard">
                                                    {loan.interest_rate == null ? '—' : `${loan.interest_rate}%`} / {loan.period == null ? '—' : `${loan.period} Mo`}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Issue Date</Text>
                                                <Text size="standard">
                                                    {formatDate(loan.issue_date)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>

                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Interest / Due Interest</Text>
                                                <Text size="standard">
                                                    {loan.interest == null ? '—' : formatCurrency(loan.interest)} / {loan.due_interest == null ? '—' : formatCurrency(loan.due_interest)}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Last Transaction</Text>
                                                <Text size="standard">
                                                    {formatDate(txDateVal)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>
                                </Columns>

                                {safeGuarantors.length > 0 && (
                                    <>
                                        <Divider />
                                        <Stack space="xsmall">
                                            <Text size="small" weight="strong" tone="secondary">Guarantors</Text>
                                            <Columns space="small" collapseBelow="tablet">
                                                {safeGuarantors.map((guarantor, index) => (
                                                    <Column key={`${loan.loan_id}-${guarantor.guarantor_type_id}-${index}`}>
                                                        <Box
                                                            padding="small"
                                                            borderRadius="standard"
                                                            style={{ border: '1px solid #f1f5f9', background: '#f8fafc' }}
                                                        >
                                                            <Stack space="xxsmall">
                                                                <Text size="xsmall" weight="strong" tone="secondary">
                                                                    {t('page.approve_mobile_app_customer.customer_details.guarantees.loan.guarantor_label', {
                                                                        defaultValue: '{{position}} Guarantor',
                                                                        position: ordinalLabel(index),
                                                                    })}
                                                                </Text>
                                                                <Text size="small">
                                                                    {(guarantor.customer_number || '—') + ' - ' + (guarantor.customer_name || '—')}
                                                                </Text>
                                                            </Stack>
                                                        </Box>
                                                    </Column>
                                                ))}
                                            </Columns>
                                        </Stack>
                                    </>
                                )}
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>
        );
    };

    const renderGuarantorForCards = (items: CustomerGuarantorItem[], emptyMessage: string) => {
        if (!items.length) {
            return (
                <Card>
                    <Box padding="large" display="flex" justifyContent="center" alignItems="center">
                        <Text tone="secondary">{emptyMessage}</Text>
                    </Box>
                </Card>
            );
        }

        return (
            <Stack space="medium">
                {items.map((loan, loanIndex) => {
                    const pastDueVal = loan.past_due_amount ?? loan.past_due;
                    const txDateVal = loan.last_transaction_date ?? loan.transaction_date;

                    return (
                        <Card key={`${loan.loan_id}-${loanIndex}`}>
                            <Stack space="medium">
                                <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                                    <Stack space="xsmall">
                                        <Inline space="small" alignY="center">
                                            <Box
                                                cursor="pointer"
                                                onClick={() => handleAccountClick(loan.loan_id, loan.loan_account_number)}
                                            >
                                                <Text tone="info" weight="strong" size="large">
                                                    {loan.loan_account_number || '—'}
                                                </Text>
                                            </Box>
                                            {!!loan.borrower_customer_number ? (
                                                <Badge tone="neutral">
                                                    Borrower: {loan.borrower_customer_number} - {loan.borrower_customer_name ?? ''}
                                                </Badge>
                                            ) : null}
                                        </Inline>
                                        <Text tone="secondary" size="small">
                                            {loan.product_name || '—'}
                                        </Text>
                                    </Stack>
                                    {pastDueVal != null && pastDueVal > 0 ? (
                                        <Badge tone="critical">Past Due</Badge>
                                    ) : (
                                        <Badge tone="positive">Active</Badge>
                                    )}
                                </Box>

                                <Divider />

                                <Columns space="medium" collapseBelow="tablet">
                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Balance</Text>
                                                <Text weight="strong" size="standard">
                                                    {loan.balance == null ? '—' : formatCurrency(loan.balance)}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Issued Amount</Text>
                                                <Text size="standard">
                                                    {loan.issued_amount == null ? '—' : formatCurrency(loan.issued_amount)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>

                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Past Due</Text>
                                                <Text
                                                    weight="strong"
                                                    size="standard"
                                                    tone={pastDueVal != null && pastDueVal > 0 ? 'critical' : 'neutral'}
                                                >
                                                    {pastDueVal == null ? '—' : formatCurrency(pastDueVal)}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Installment</Text>
                                                <Text size="standard">
                                                    {loan.installment == null ? '—' : formatCurrency(loan.installment)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>

                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Interest Rate / Period</Text>
                                                <Text size="standard">
                                                    {loan.interest_rate == null ? '—' : `${loan.interest_rate}%`} / {loan.period == null ? '—' : `${loan.period} Mo`}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Issue Date</Text>
                                                <Text size="standard">
                                                    {formatDate(loan.issue_date)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>

                                    <Column>
                                        <Stack space="xsmall">
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Interest / Due Interest</Text>
                                                <Text size="standard">
                                                    {loan.interest == null ? '—' : formatCurrency(loan.interest)} / {loan.due_interest == null ? '—' : formatCurrency(loan.due_interest)}
                                                </Text>
                                            </Stack>
                                            <Stack space="xsmall">
                                                <Text tone="secondary" size="xsmall">Last Transaction</Text>
                                                <Text size="standard">
                                                    {formatDate(txDateVal)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Column>
                                </Columns>

                                <Divider />

                                <Box
                                    padding="small"
                                    borderRadius="standard"
                                    style={{ border: '1px solid #e2e8f0', background: '#f8fafc', alignSelf: 'flex-start' }}
                                >
                                    <Text size="small" weight="strong">
                                        Guarantor Type: <span style={{ color: '#0f172a' }}>{loan.guarantor?.guarantor_type || 'Guarantor'}</span>
                                    </Text>
                                </Box>
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>
        );
    };

    if (loading) {
        return (
            <Loading
                message={t('page.approve_mobile_app_customer.customer_details.guarantees.loading', {
                    defaultValue: 'Loading guarantees data...',
                })}
            />
        );
    }

    if (error) {
        return (
            <Card>
                <Box padding="large" display="flex" justifyContent="center" alignItems="center">
                    <Text tone="critical">{error}</Text>
                </Box>
            </Card>
        );
    }

    return (
        <>
            <Stack space="medium">
                <Card>
                    <Stack space="medium">
                        <Text weight="strong" size="large">
                            {t('page.approve_mobile_app_customer.customer_details.guarantees.title', {
                                defaultValue: 'Guarantees',
                            })}
                        </Text>
                        <TabsProvider>
                            <Stack space="medium">
                                <Tabs
                                    label={t('page.approve_mobile_app_customer.customer_details.guarantees.tabs.label', {
                                        defaultValue: 'Guarantees tabs',
                                    })}
                                    divider="full"
                                >
                                    <Tab>
                                        {t('page.approve_mobile_app_customer.customer_details.guarantees.tabs.for_me', {
                                            defaultValue: 'Guarantors for loans',
                                        })}
                                    </Tab>

                                    <Tab>
                                        {t('page.approve_mobile_app_customer.customer_details.guarantees.tabs.i_am_guarantor', {
                                            defaultValue: 'Guaranteed for loans',
                                        })}
                                    </Tab>

                                </Tabs>

                                <Box>
                                    <Checkbox
                                        checked={showClosedLoans}
                                        onChange={(e) => setShowClosedLoans(e.currentTarget.checked)}
                                        label={t('page.approve_mobile_app_customer.customer_details.guarantees.show_closed', { defaultValue: 'Show closed loans' })}
                                    />
                                </Box>

                                <TabPanels>
                                    <TabPanel>
                                        {(() => {
                                            const myLoans = loanData?.my_loans ?? [];
                                            const statusFiltered = myLoans.filter((l) => {
                                                const status = (l as any).status ?? (l as any).loan_status ?? 1;
                                                const balance = (l as any).balance ?? 0;
                                                return showClosedLoans
                                                    ? balance == 0
                                                    : status === 1 && balance > 0;
                                            });

                                            const filtered = statusFiltered.filter((l) => {
                                                const gs = Array.isArray(l.guarantors) ? l.guarantors : [];
                                                // show loan only if at least one guarantor entry exists (has customer_number or exists===true)
                                                return gs.some((g: any) => !!g?.customer_number || g?.exists === true);
                                            });

                                            return renderMyLoanCards(
                                                filtered,
                                                t('page.approve_mobile_app_customer.customer_details.guarantees.empty.for_me', {
                                                    defaultValue: 'No loan guarantor records available for customer {{number}}.',
                                                    number: customerNumber,
                                                })
                                            );
                                        })()}
                                    </TabPanel>
                                    <TabPanel>
                                        {(() => {
                                            const guarantorLoans = loanData?.i_am_guarantor_for ?? [];
                                            const statusFiltered = guarantorLoans.filter((l) => {
                                                const status = (l as any).status ?? (l as any).loan_status ?? 1;
                                                const balance = (l as any).balance ?? 0;
                                                return showClosedLoans
                                                    ? balance == 0
                                                    : status === 1 && balance > 0;
                                            });

                                            return renderGuarantorForCards(
                                                statusFiltered,
                                                t('page.approve_mobile_app_customer.customer_details.guarantees.empty.i_am_guarantor', {
                                                    defaultValue: 'Customer {{number}} is not a guarantor for other loans.',
                                                    number: customerNumber,
                                                })
                                            );
                                        })()}
                                    </TabPanel>
                                </TabPanels>
                            </Stack>
                        </TabsProvider>
                    </Stack>
                </Card>
            </Stack>

            <Drawer
                title={selectedAccountNumber ? `Account Number - ${selectedAccountNumber}` : 'Transactions'}
                width="large"
                open={transactionsOpen}
                onClose={() => setTransactionsOpen(false)}
            >
                <Stack space="medium">
                    {transactionsLoading ? (
                        <Text tone="secondary">Loading transactions...</Text>
                    ) : (
                        <Table label="Account transactions">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Updated Date</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        <Text size="xsmall">Transaction Number</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Debit</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Credit</Text>
                                    </TableHeaderCell>
                                    <TableHeaderCell align="right">
                                        <Text size="xsmall">Balance</Text>
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {transactions.length > 0 ? (
                                    transactions.map((transaction) => (
                                        <TableRow key={transaction.id}>
                                            <TableCell>
                                                <Text size="small">{formatDate(transaction.updated_date)}</Text>
                                            </TableCell>
                                            <TableCell>
                                                <Text size="small">{transaction.transaction_number ?? '-'}</Text>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Text size="small">{formatCurrency(transaction.debit ?? 0)}</Text>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Text size="small">{formatCurrency(transaction.credit ?? 0)}</Text>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Text size="small">{formatCurrency(transaction.balance ?? 0)}</Text>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colspan={5} align="center">
                                            <Text size="small" tone="secondary">No transactions found.</Text>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Stack>
            </Drawer>
        </>
    );
};

export default GuaranteesTab;
